import { useEffect, useState } from "react";
import { MonitorUp } from "lucide-react";
import { Button, Card, CardTitle } from "@clevis/shared-ui";
import { useRemoteSupport } from "./RemoteSupportProvider";

// The Support page's remote-session card: start → read the code to support →
// share screen. All state lives in RemoteSupportProvider so navigation
// doesn't drop the share.
export function RemoteSupportCard() {
  const rs = useRemoteSupport();
  const [left, setLeft] = useState("");

  useEffect(() => {
    if (!rs?.expiresAtMs || rs.status !== "pending") return;
    const tick = () => {
      const ms = Math.max(0, rs.expiresAtMs! - Date.now());
      setLeft(`${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [rs?.expiresAtMs, rs?.status]);

  if (!rs) return null;

  return (
    <Card>
      <CardTitle className="flex items-center gap-2">
        <MonitorUp size={16} /> Remote session with support
      </CardTitle>
      {!rs.canShare ? (
        <p className="text-xs text-steel">
          This browser can't share its screen. Use Chrome, Edge or Safari on a computer.
        </p>
      ) : rs.sessionId == null ? (
        <>
          <p className="mb-3 text-xs text-steel">
            Let Clevis support see your screen — and, with your permission, click and type for you.
            You stay in control and can end it any time.
          </p>
          <Button onClick={() => void rs.start()} busy={rs.busy}>
            Start remote session
          </Button>
        </>
      ) : rs.status === "pending" && !rs.sharing ? (
        <div className="space-y-3">
          <p className="text-xs text-steel">Read this code to the support person on the phone:</p>
          <div className="text-center font-mono text-3xl font-bold tracking-[0.3em] text-ink">
            {rs.code?.slice(0, 3)}-{rs.code?.slice(3)}
          </div>
          <p className="text-center text-[11px] text-steel">Code expires in {left}</p>
          <div className="flex gap-2">
            <Button onClick={() => void rs.share()} className="flex-1">
              Share your screen
            </Button>
            <Button variant="ghost" onClick={() => void rs.end()}>
              Cancel
            </Button>
          </div>
          <p className="text-[11px] text-steel">
            When the picker opens, choose <strong>this tab</strong> so support can also click for you —
            sharing a window or screen is view-only.
          </p>
        </div>
      ) : rs.sharing ? (
        <div className="space-y-2">
          <p className="text-sm text-ink">
            <span className="mr-1 inline-block h-2 w-2 animate-pulse rounded-full bg-danger" /> You're
            sharing your screen{rs.shareSurface === "browser" ? " (support can click for you)" : " (view-only)"}.
          </p>
          <Button variant="danger" onClick={() => void rs.end()}>
            End session
          </Button>
        </div>
      ) : (
        <p className="text-xs text-steel">Connecting…</p>
      )}
      {rs.error ? <p className="mt-2 text-xs text-danger">{rs.error}</p> : null}
    </Card>
  );
}
